import { Optional } from '..';
import { BaseQueryParams } from '../../Global.model';
import { FuelSurchargeTableAttributes } from '../fuelSurchargeTable/fuelSurchargeTable.model';
import { InvoiceService, InvoiceServiceUnit } from '../invoiceService/invoiceService.model';
import { AttachmentBasicProps } from '../orders/orders.model';
import { AttachmentFile } from '../otherExpenseAttachment/otherExpenseAttachment.model';

export type RateTableQboAttributes = {
  division_qbo_credentials_id?: number | null;
};

export interface RateTableSearchParams {
  customer_id: string;
  search?: string;
  pickup_date?: string;
  start_date?: string;
  end_date?: string;
  status?: RateTableStatus;
  division_id?: string;
}

export interface SearchRateTablesParams {
  filters?: RateTableSearchParams;
  params?: BaseQueryParams;
}

export enum RateTableStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}

export interface RateTableItemAttributes {
  id: string;
  rate_table_id: string;
  invoice_service_id: string | null;
  qbo_service_id: string | null;
  unit: InvoiceServiceUnit;
  quantity: number | null;
  rate: number;
  tenant_id: string;
  service_name: string | null;
  sort_order: number;
  created_at: Date;
  updated_at: Date;
  deleted_at: Date | null;

  // Relationships
  rate_table?: RateTableAttributes;
  invoice_service?: InvoiceService;
}

export interface RateTableAttributes {
  id: string;
  customer_id: string;
  name: string;
  division_id: string;
  start_date: Date;
  end_date: Date | null;
  fuel_surcharge_table_id: string | null;
  unit: InvoiceServiceUnit | null;
  overwritten_mileage: number | null;
  note: string | null;
  document_id: string | null;
  tenant_id: string;
  qbo_attributes: RateTableQboAttributes | null;
  created_at: Date;
  updated_at: Date;
  deleted_at: Date | null;

  // Virtual columns
  is_division_connected_to_qbo?: boolean;
  number_of_orders_linked?: number;

  // Relationships
  rate_table_services?: RateTableItemAttributes[];
  document?: AttachmentBasicProps | null;
}

export type RateTableVirtualColumns = 'is_division_connected_to_qbo' | 'number_of_orders_linked';

export type RateTableTimestamps = 'created_at' | 'updated_at' | 'deleted_at';

export type RateTableNonCreationalOrEditableAttributes =
  | 'id'
  | 'qbo_attributes'
  | RateTableVirtualColumns
  | RateTableTimestamps;

export type RateTableNullableAttributes =
  | 'end_date'
  | 'fuel_surcharge_table_id'
  | 'unit'
  | 'overwritten_mileage'
  | 'note'
  | 'document_id';

export type RateTableCreationAttributes = Omit<
  Optional<RateTableAttributes, RateTableNullableAttributes>,
  RateTableNonCreationalOrEditableAttributes
>;

export type RateTableUpdateAttributes = Partial<RateTableCreationAttributes>;

export type RateTableItemTimestamps = 'created_at' | 'updated_at' | 'deleted_at';

export type RateTableItemNonCreationalOrEditableAttributes =
  | 'id'
  | RateTableItemTimestamps
  | 'sort_order';

export type RateTableItemNullableAttributes =
  | 'invoice_service_id'
  | 'qbo_service_id'
  | 'service_name'
  | 'deleted_at'
  | 'quantity';

export type RateTableItemCreationAttributes = Omit<
  Optional<RateTableItemAttributes, RateTableItemNullableAttributes>,
  RateTableItemNonCreationalOrEditableAttributes
>;

export type RateTableItemFullUpdateAttributes = Omit<
  RateTableItemAttributes,
  RateTableItemNonCreationalOrEditableAttributes
> & {
  id?: string;
};

export type RateTableItemUpdateAttributes = Partial<RateTableItemCreationAttributes>;

export interface SearchRateTablesResponseDto
  extends Omit<RateTableAttributes, RateTableTimestamps | 'document'> {
  services: (Omit<RateTableItemAttributes, RateTableItemTimestamps> & {
    invoice_service: RateTableItemAttributes['invoice_service'];
  })[];
  document?: AttachmentFile | null;
  fuel_surcharge_table?: Omit<
    FuelSurchargeTableAttributes,
    'fuel_surcharge_table_prices' | 'fuel_surcharge_rate_ranges'
  > & {
    prices: FuelSurchargeTableAttributes['fuel_surcharge_table_prices'];
    rate_ranges: FuelSurchargeTableAttributes['fuel_surcharge_rate_ranges'];
  };
}

export type AddRateTableResponseDto = {
  data: Omit<RateTableAttributes, RateTableTimestamps>;
};

export type AddRateTableFormikDto = Omit<
  RateTableCreationAttributes,
  'tenant_id' | 'start_date' | 'end_date' | 'division_id' | 'customer_id'
> & {
  start_date: string | Date | null;
  end_date: string | Date | null;
  division_id: string | null;
  services: (RateTableItemAttributes & {
    ui_id: string;
    invoice_service: InvoiceService;
    service_name: string;
    initial_service_option?: {
      id: string;
      label: string;
      value: unknown;
    };
  })[];
  services_to_delete: string[];
  is_document_deleted: boolean;
};

export type AddRateTableRequestDto = Omit<RateTableCreationAttributes, 'tenant_id'> & {
  services: Omit<RateTableItemCreationAttributes, 'tenant_id' | 'rate_table_id'>[];
};

export type UpdateRateTableRequestDto = Omit<RateTableCreationAttributes, 'tenant_id'> & {
  id?: string;
  services: Omit<RateTableItemFullUpdateAttributes, 'tenant_id' | 'rate_table_id'>[];
  services_to_delete: string[];
  is_document_deleted: boolean;
};

export type AddRateTableDocumentRequestDto = {
  file: File;
  rateTableId: string;
};
